import type {
  LifeMusicPlaylist,
  LifeFoodEntry,
  LifePhotoEntry,
  LifeBookNote,
  LifeSignalLog,
  SpotifyNowPlaying,
  SpotifyTrack,
  SpotifyPlaylist,
  SpotifyArtist,
} from '@tinkern/content'

export type {
  LifeMusicPlaylist,
  LifeFoodEntry,
  LifePhotoEntry,
  LifeBookNote,
  LifeSignalLog,
  SpotifyNowPlaying,
  SpotifyTrack,
  SpotifyPlaylist,
  SpotifyArtist,
}

const API_BASE = (import.meta as any).env?.VITE_API_BASE ?? ''
const BUILD_ID = (import.meta as any).env?.VITE_BUILD_ID || ''
const v = BUILD_ID ? `?v=${BUILD_ID}` : ''

async function getJson<T>(p: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${API_BASE}${p}${v}`)
    if (!res.ok) return fallback
    return (await res.json()) as T
  } catch {
    return fallback
  }
}

// Static JSON — served from public/api/ (generated by prebuild)
export const fetchMusic = () => getJson<LifeMusicPlaylist[]>('/api/life/music.json', [])
export const fetchFood = () => getJson<LifeFoodEntry[]>('/api/life/food.json', [])
export const fetchPhotos = () => getJson<LifePhotoEntry[]>('/api/life/photos.json', [])
export const fetchBooks = () => getJson<LifeBookNote[]>('/api/life/books.json', [])
export const fetchSignalLog = () =>
  getJson<LifeSignalLog | null>('/api/life/now.json', null)

// Spotify API — proxied through /api/music serverless function
const MUSIC_API = `${API_BASE}/api/music`

export const fetchNowPlaying = (): Promise<SpotifyNowPlaying> =>
  fetch(`${MUSIC_API}?type=now-playing`).then(r => r.json())

export const fetchAllPlaylists = (): Promise<SpotifyPlaylist[]> =>
  fetch(`${MUSIC_API}?type=playlists`).then(r => r.json())

export const fetchTopTracks = (): Promise<SpotifyTrack[]> =>
  fetch(`${MUSIC_API}?type=top-tracks`).then(r => r.json())

export const fetchTopArtists = (): Promise<SpotifyArtist[]> =>
  fetch(`${MUSIC_API}?type=top-artists`).then(r => r.json())

export const fetchPlaylistDetail = (id: string): Promise<{ id: string | null; trackCount: number | null }> =>
  fetch(`${MUSIC_API}?type=playlist-detail&id=${encodeURIComponent(id)}`).then(r => r.json())
